import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import { Input, LinearProgress } from '@mui/material';

import { fbmApi as api } from 'api/fbm';
import { mmFetch } from 'api/mmFetch';
import { UserContext } from 'contexts/UserContext';
import { Notifications, useDebouncedValue } from 'shared';
import { fileDownload } from 'shared/components/fileDownload';
import { useGetOrderDeepDiveQuery } from 'shared/hooks/fbm/useGetOrderDeepDiveQuery';

import { DeepDiveResults } from './deepDiveResults';

/**
 *
 * @param {string} fbmOrderNum - The FBM order number to search for
 * @param {Function} [onFbmOrderNumChange] - Callback when search term changes (updates URL query params)
 */
function ViewOrderDeepDive({ fbmOrderNum, onFbmOrderNumChange }) {
    const [notification, setNotification] = useState({ text: '', level: '' });
    const { fbmAccount } = useContext(UserContext);
    const [isIngesting, setIsIngesting] = useState(false);
    const [downloadingPackList, setDownloadingPackList] = useState(false);
    const [searchTerm, setSearchTerm] = useState(fbmOrderNum);
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

    const {
        data: deepDiveResults,
        error: errorDeepDiveResults,
        isFetching: isFetchingDeepDiveResults,
        isLoading: isLoadingDeepDiveResults,
    } = useGetOrderDeepDiveQuery(debouncedSearchTerm, fbmAccount.fbmAccountName);

    // Sync searchTerm when fbmOrderNum prop changes (e.g., URL navigation)
    useEffect(() => {
        setSearchTerm(fbmOrderNum);
    }, [fbmOrderNum]);

    // Update URL query params when debounced search term changes
    useEffect(() => {
        onFbmOrderNumChange?.(debouncedSearchTerm || undefined);
    }, [debouncedSearchTerm, onFbmOrderNumChange]);

    useEffect(() => {
        setNotification({ text: errorDeepDiveResults?.message, level: 'error' });
    }, [errorDeepDiveResults]);

    const downloadRetailPackListReport = async () => {
        setDownloadingPackList(true);
        try {
            const partnerOrderNumber = deepDiveResults?.orderNum;
            if (!partnerOrderNumber) {
                setNotification({ text: 'Partner order number not available', level: 'error' });
                return;
            }
            const response = await mmFetch(
                `api/fbm/smalls/v1/pack-list-report?partnerOrderNumber=${encodeURIComponent(partnerOrderNumber)}&fbmAccount=${fbmAccount.fbmAccountName}`,
            );
            if (response.ok) {
                await fileDownload(response, `pack-list-report-${partnerOrderNumber}.pdf`);
            } else {
                setNotification({ text: 'Error downloading pack list report', level: 'error' });
            }
        } catch {
            setNotification({ text: 'Error downloading pack list report', level: 'error' });
        } finally {
            setDownloadingPackList(false);
        }
    };

    return (
        <div>
            <h1>{fbmAccount.companyName} - Order Deep Dive</h1>
            <Notifications options={notification} />
            {!!fbmAccount.id && (
                <div>
                    <Grid container>
                        {Object.keys(deepDiveResults ?? {}).length > 0 && (
                            <Grid item xs={4}>
                                <Button
                                    id="MarkShippedButton"
                                    onClick={async () => {
                                        const response = await api.markOrderShipped(
                                            deepDiveResults.shipmentID,
                                            fbmAccount.fbmAccountName,
                                        );
                                        if (response.status === false) {
                                            setNotification({
                                                text: `Error marking shipped ${response.msg}`,
                                                level: 'error',
                                            });
                                            return;
                                        }

                                        setNotification({ text: 'Success', level: 'success' });
                                    }}
                                >
                                    Mark Order Shipped
                                </Button>
                            </Grid>
                        )}
                        {Object.keys(deepDiveResults ?? {}).length > 0 && (
                            <Grid item xs={4}>
                                <Button
                                    id="IngestButton"
                                    disabled={isIngesting || fbmAccount.fbmAccountName === 'goodhillfarms'}
                                    onClick={async () => {
                                        setIsIngesting(true);
                                        const response = await api.ingestSingleOrder(
                                            deepDiveResults.orderNum,
                                            fbmAccount.fbmAccountName,
                                        );
                                        setIsIngesting(false);
                                        if (response.status === false) {
                                            setNotification({
                                                text: `Unable to ingest ${response.msg}`,
                                                level: 'error',
                                            });
                                            return;
                                        }

                                        setNotification({ text: 'Success', level: 'success' });
                                    }}
                                >
                                    Ingest
                                </Button>
                            </Grid>
                        )}
                        {deepDiveResults?.ext?.['smalls.downloadRetailPackListReport'] && (
                            <Grid item xs={4}>
                                {downloadingPackList ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Button id="DownloadPackListButton" onClick={downloadRetailPackListReport}>
                                        Download Pack List Report
                                    </Button>
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12} className="flex flex-col items-center justify-center gap-1 !mt-[40px]">
                            <Input
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                }}
                                className="w-[450px]"
                                placeholder="Enter order number"
                            />
                            <span className="text-xs text-gray-500">Search runs automatically as you type</span>
                        </Grid>
                    </Grid>
                    <br />
                    <br />
                    {(isLoadingDeepDiveResults || isFetchingDeepDiveResults) && <LinearProgress />}
                    {Object.keys(deepDiveResults ?? {}).length > 0 && (
                        <DeepDiveResults companyName={fbmAccount.companyName} deepDiveResults={deepDiveResults} />
                    )}
                </div>
            )}
        </div>
    );
}

export { ViewOrderDeepDive };
